import { useLayoutEffect, type ReactElement } from "react";
import { Link } from "react-router";
import { useAuth } from "@/core/auth/auth-provider";
import { Button } from "@/shared/components/ui";
import {
  ContactSupportAction,
  OPEN_INVOICES_LOGIN_PATH,
  PUBLIC_ACTIONS_CLASS,
  PUBLIC_HEADING_CLASS,
  ViewInvoicesAction,
  useHeadingFocus,
} from "./invoice-surface-states";
import { scrubAddressBar } from "./locator-entry";
import { PayPublicChrome } from "./pay-public-chrome";

/**
 * 006-9 UI contract §4 — the neutral guidance surface for payment links that
 * could not be opened. It never shows invoice, amount, person, company, or
 * correlation detail; both realms render the same heading and chrome.
 */

export const GUIDANCE_HEADING = "We couldn't open that payment link";
export const GUIDANCE_BODY =
  "The link may have expired or been replaced. Sign in to find your open invoices, or contact support.";
/** §4 amendment 2026-09-26: the body follows the SESSION state, never the locator outcome. */
export const SIGNED_IN_GUIDANCE_BODY =
  "The link may have expired or been replaced. Find the invoice under My invoices, or contact support.";
export const ADMIN_GUIDANCE_BODY =
  "Payment links can't be used in this application. Sign out of this administrative session, then open your link again.";
export const SIGN_IN_ACTION_LABEL = "Sign in";
const ADMIN_PAY_PATH = "/pay";

function GuidanceCard({ body, actions }: { body: string; actions: ReactElement }): ReactElement {
  const headingRef = useHeadingFocus();
  return (
    <PayPublicChrome>
      <h1 ref={headingRef} tabIndex={-1} className={PUBLIC_HEADING_CLASS}>
        {GUIDANCE_HEADING}
      </h1>
      <p className="mt-2 text-base text-gray-600">{body}</p>
      {/* Auth-surface primary CTA rule: the filled guidance action is
          full-width Large; the secondary stays auto-width beneath it. */}
      <div className={PUBLIC_ACTIONS_CLASS}>{actions}</div>
    </PayPublicChrome>
  );
}

/** Standard-realm guidance (`/pay/guidance`). */
export function PayGuidancePage(): ReactElement {
  const auth = useAuth();
  // The provider keeps `session` only while authenticated (including the
  // transient `refreshing`/`returning` flips and an active acting session);
  // a sign-out in flight is the one authenticated-looking state to exclude.
  const signedIn = auth.session !== undefined && auth.status !== "logout";
  const primary = signedIn ? (
    <ViewInvoicesAction className="w-full" />
  ) : (
    <Button asChild className="w-full">
      <Link to={OPEN_INVOICES_LOGIN_PATH}>{SIGN_IN_ACTION_LABEL}</Link>
    </Button>
  );
  return (
    <GuidanceCard
      body={signedIn ? SIGNED_IN_GUIDANCE_BODY : GUIDANCE_BODY}
      actions={
        <>
          {primary}
          <ContactSupportAction />
        </>
      }
    />
  );
}

/** Admin-realm variant for every `/pay/*` path — no member requests, no sign-in. */
export function PayGuidanceAdminRealmPage(): ReactElement {
  // A locator or navigation reference may still be in the address bar; drop
  // it in the layout phase of the first commit, before paint, exactly like
  // the landing route.
  useLayoutEffect(() => {
    scrubAddressBar(ADMIN_PAY_PATH);
  }, []);
  return <GuidanceCard body={ADMIN_GUIDANCE_BODY} actions={<ContactSupportAction />} />;
}
